import {
    nfcEnabledPayload,
    nfcReadTagPayload,
    nfcSupportedPayload,
    biometricAccessPayload,
    postMessageToNativeApp,
    sigurScanAvailablePayload,
    sigurRequestPermissionsPayload,
    locationServiceDefaultPayload,
    locationServiceStatusPayload,
    locationServiceCurrentPositionPayload,
    locationServiceVisibleBleTagsPayload,
    removeKeyFromStoragePayload,
    getAppVersionPayload,
    hapticPayload,
    HapticImpactStyle,
    HapticNotificationType,
} from './post-message';
import {defaultNativeAppResponse} from './post-message';

// const TIMEOUT = 200;

/*
	Types
*/
type SetItemType = (
    keyName: string,
    keyValue: string,
) => Promise<defaultNativeAppResponse>;
type GetItemType = (keyName: string) => Promise<string>;
type ClearType = () => Promise<defaultNativeAppResponse>;
type RemoveItemType = (keyName: string) => Promise<removeKeyFromStoragePayload>;

export type BridgeStorage = {
    setItem: SetItemType;
    getItem: GetItemType;
    clear: ClearType;
    removeItem: RemoveItemType;
};

type BridgeNfcIsSupportedType = () => Promise<nfcSupportedPayload>;
type BridgeNfcIsEnabledType = () => Promise<nfcEnabledPayload>;
type BridgeNfcReadTagType = () => Promise<nfcReadTagPayload>;
type BridgeNfcStopReadingType = () => Promise<defaultNativeAppResponse>;
type BridgeNfcOpenSettingsType = () => Promise<defaultNativeAppResponse>;
export type BridgeNfc = {
    isSupported: BridgeNfcIsSupportedType;
    isEnabled: BridgeNfcIsEnabledType;
    readTag: BridgeNfcReadTagType;
    stopReading: BridgeNfcStopReadingType;
    openSettings: BridgeNfcOpenSettingsType;
};
export type BridgeHaptic = {
    impact: (style?: HapticImpactStyle) => Promise<defaultNativeAppResponse>;
    notification: (
        type: HapticNotificationType,
    ) => Promise<defaultNativeAppResponse>;
    selection: () => Promise<defaultNativeAppResponse>;
};
type BiometricAccessAuthenticateType = () => Promise<biometricAccessPayload>;
export type BiometricAccessType = {
    authenticate: BiometricAccessAuthenticateType;
};
export type ScreenCaptureType = {
    enable: () => Promise<defaultNativeAppResponse>;
    disable: () => Promise<defaultNativeAppResponse>;
};
export type NotificationsType = {
    enable: () => Promise<defaultNativeAppResponse>;
    disable: () => Promise<defaultNativeAppResponse>;
    isEnabled: () => Promise<'true' | 'false' | 'undefined'>;
};
type BridgeSigurScanAvailableType = () => Promise<sigurScanAvailablePayload>;
type BridgeSigurRequestPermissionsType =
    () => Promise<sigurRequestPermissionsPayload>;
type BridgeSigurStartAccessType = () => Promise<defaultNativeAppResponse>;
type BridgeSigurStopAccessType = () => Promise<defaultNativeAppResponse>;
export type BridgeSigur = {
    isScanAvailable: BridgeSigurScanAvailableType;
    requestPermissions: BridgeSigurRequestPermissionsType;
    startAccess: BridgeSigurStartAccessType;
    stopAccess: BridgeSigurStopAccessType;
};

type BridgeLocationServiceIsReadyType =
    () => Promise<locationServiceDefaultPayload>;
type BridgeLocationServiceRequestIndoorPermissionsType =
    () => Promise<locationServiceDefaultPayload>;
type BridgeLocationServiceRequestGpsPermissionsType =
    () => Promise<locationServiceDefaultPayload>;
type BridgeLocationServiceStartType =
    () => Promise<locationServiceDefaultPayload>;
type BridgeLocationServiceStopType =
    () => Promise<locationServiceDefaultPayload>;
type BBridgeLocationServiceStatusType =
    () => Promise<locationServiceStatusPayload>;
type BridgeLocationServiceIsGpsReadyType =
    () => Promise<locationServiceDefaultPayload>;
type BridgeLocationServiceIsBleReadyType =
    () => Promise<locationServiceDefaultPayload>;
type BridgeLocationServiceGetCurrentPositionType =
    () => Promise<locationServiceCurrentPositionPayload>;
type BridgeLocationServiceGetVisibleTagsType = (params?: {
    from?: number;
    to?: number;
}) => Promise<locationServiceVisibleBleTagsPayload>;
export type BridgeLocationService = {
    isReady: BridgeLocationServiceIsReadyType;
    requestIndoorPermissions: BridgeLocationServiceRequestIndoorPermissionsType;
    requestGpsPermissions: BridgeLocationServiceRequestGpsPermissionsType;
    start: BridgeLocationServiceStartType;
    stop: BridgeLocationServiceStopType;
    status: BBridgeLocationServiceStatusType;
    isGpsReady: BridgeLocationServiceIsGpsReadyType;
    isBleReady: BridgeLocationServiceIsBleReadyType;
    getCurrentPosition: BridgeLocationServiceGetCurrentPositionType;
    getVisibleTags: BridgeLocationServiceGetVisibleTagsType;
};
/*
	Methods
*/

export const getCurrentLanguage = (): Promise<string> =>
    postMessageToNativeApp({
        type: 'GET_CURRENT_LANGUAGE',
    });

export const getCurrentTheme = (): Promise<'light' | 'dark'> =>
    postMessageToNativeApp({
        type: 'GET_CURRENT_THEME',
    });

/**
 * Returns the marketing version of the native Qollab application.
 */
export const getAppVersion = (): Promise<getAppVersionPayload> =>
    postMessageToNativeApp({
        type: 'GET_APP_VERSION',
    });

export const isCorporateDevice = (): Promise<boolean> =>
    postMessageToNativeApp({
        type: 'IS_CORPORATE_DEVICE',
    });
/**
 * Scan QR code using the app.
 */
export const getQr = (): Promise<string> =>
    postMessageToNativeApp({
        type: 'GET_QR',
    });

/**
 * Copy text to clipboard of the app.
 * @param text - string to clipboard
 * @returns A promise that resolves with an object containing the response code and an optional message or reason.
 */
export const copyToClipboard = (
    text: string,
): Promise<defaultNativeAppResponse> =>
    postMessageToNativeApp({
        type: 'COPY_TO_CLIPBOARD',
        payload: {
            text,
        },
    });

/**
 * Share text using the native app
 * @param text - string to share
 * @returns A promise that resolves with an object containing the response code and an optional message or reason.
 */
export const share = (text?: string): Promise<defaultNativeAppResponse> =>
    postMessageToNativeApp({
        type: 'SHARE',
        payload: {
            text,
        },
    });

/**
 * Share files using the app.
 * @param {string} text - The string message that will be passed to the native app's share menu as a caption to the file.
 * @param {string} filename - The filename with extension.
 * @param {string} base64data - The file in base64 format.
 * @returns {Promise<{ code: number, message?: string, reason?: string }>} A promise that resolves with an object containing the response code and an optional message or reason.
 */
export const shareFile = (
    text: string,
    filename: string,
    based64data: string,
): Promise<defaultNativeAppResponse> =>
    postMessageToNativeApp({
        type: 'SHARE_FILE',
        payload: {
            text,
            filename,
            based64data,
        },
    });

/**
 * Object contains methods for notifications.
 */
export const notifications: NotificationsType = {
    /**
     * Enable notification only for the current mini application.
     * @returns {Promise<{ code: number, message?: string, reason?: string }>} A promise that resolves with an object containing the response code and an optional message or reason.
     */
    enable: () =>
        postMessageToNativeApp({
            type: 'ENABLE_NOTIFICATIONS',
        }),
    /**
     * Disable notification only for the current mini application.
     * @returns {Promise<{ code: number, message?: string, reason?: string }>} A promise that resolves with an object containing the response code and an optional message or reason.
     */
    disable: () =>
        postMessageToNativeApp({
            type: 'DISABLE_NOTIFICATIONS',
        }),
    isEnabled: () =>
        postMessageToNativeApp({
            type: 'GET_NOTIFICATIONS_STATE',
        }),
};

/**
 * Object contains methods to enable or disable screen capture.
 */
export const screenCapture: ScreenCaptureType = {
    disable: () =>
        postMessageToNativeApp({
            type: 'DISABLE_SCREEN_CAPTURE',
        }),
    enable: () =>
        postMessageToNativeApp({
            type: 'ENABLE_SCREEN_CAPTURE',
        }),
};

/**
 * An object consisting of functions specifically crafted for managing application secured storage.
 */
export const storage: BridgeStorage = {
    /**
     * Save key value pair items to native app's secure storage.
     * @param {string} keyName - The key for the item.
     * @param {string} keyValue - The value to be stored.
     * @returns {Promise<{ code: number, message?: string, reason?: string }>} A promise that resolves with an object containing the response code and an optional message or reason.
     */
    setItem: (keyName, keyValue) =>
        postMessageToNativeApp({
            type: 'SET_STORAGE',
            payload: {
                keyName,
                keyValue,
            },
        }),
    /**
     * Get previously saved value by item's key from native app's secure storage.
     * @param {string} keyName - The key of the item to retrieve.
     * @returns {Promise<{ code: number, message?: string, reason?: string, value?: string }>} A promise that resolves with an object containing the response code and an optional message, reason, and value.
     */
    getItem: (keyName) =>
        postMessageToNativeApp({
            type: 'GET_STORAGE',
            payload: {
                keyName,
            },
        }),
    /**
     * Clear native app's secure storage.
     * @returns {Promise<{ code: number, message?: string, reason?: string }>} A promise that resolves with an object containing the response code and an optional message or reason.
     */
    clear: () =>
        postMessageToNativeApp({
            type: 'CLEAR_STORAGE',
        }),
    /**
     * Remove specific key from storage.
     * @returns {Promise<{ code: number, message?: string, reason?: string }>} A promise that resolves with an object containing the response code and an optional message or reason.
     */
    removeItem: (keyName) =>
        postMessageToNativeApp({
            type: 'REMOVE_KEY_FROM_STORAGE',
            payload: {
                keyName,
            },
        }),
};

/**
 * Accesses the settings menu of the native app.
 * @returns {Promise<{ code: number, message?: string, reason?: string }>} A promise that resolves with an object containing the response code and an optional message or reason.
 */
export const openSettings = (): Promise<defaultNativeAppResponse> =>
    postMessageToNativeApp({
        type: 'OPEN_SETTINGS',
    });

export const nfc: BridgeNfc = {
    isSupported: () =>
        postMessageToNativeApp({
            type: 'NFC_SUPPORTED',
        }),
    isEnabled: () =>
        postMessageToNativeApp({
            type: 'NFC_ENABLED',
        }),
    readTag: () =>
        postMessageToNativeApp({
            type: 'NFC_READ_TAG',
        }),
    stopReading: () =>
        postMessageToNativeApp({
            type: 'NFC_STOP_READING',
        }),
    openSettings: () =>
        postMessageToNativeApp({
            type: 'NFC_OPEN_SETTINGS',
        }),
};

export const biometricAccess: BiometricAccessType = {
    authenticate: () =>
        postMessageToNativeApp({
            type: 'BIOMETRIC_ACCESS',
        }),
};

export const sigur: BridgeSigur = {
    isScanAvailable: () =>
        postMessageToNativeApp({
            type: 'SIGUR_IS_SCAN_AVAILABLE',
        }),
    requestPermissions: () =>
        postMessageToNativeApp({
            type: 'SIGUR_REQUEST_PERMISSIONS',
        }),
    startAccess: () =>
        postMessageToNativeApp({
            type: 'SIGUR_START_ACCESS',
        }),
    stopAccess: () =>
        postMessageToNativeApp({
            type: 'SIGUR_STOP_ACCESS',
        }),
};

export const locationService: BridgeLocationService = {
    isReady: () =>
        postMessageToNativeApp({
            type: 'LOCATION_SERVICE_IS_READY',
        }),
    requestIndoorPermissions: () =>
        postMessageToNativeApp({
            type: 'LOCATION_SERVICE_REQUEST_INDOOR_PERMISSION',
        }),
    requestGpsPermissions: () =>
        postMessageToNativeApp({
            type: 'LOCATION_SERVICE_REQUEST_GPS_PERMISSION',
        }),
    start: () =>
        postMessageToNativeApp({
            type: 'LOCATION_SERVICE_START',
        }),
    stop: () =>
        postMessageToNativeApp({
            type: 'LOCATION_SERVICE_STOP',
        }),
    status: () =>
        postMessageToNativeApp({
            type: 'LOCATION_SERVICE_STATUS',
        }),
    isGpsReady: () =>
        postMessageToNativeApp({
            type: 'LOCATION_SERVICE_GPS_IS_READY',
        }),
    isBleReady: () =>
        postMessageToNativeApp({
            type: 'LOCATION_SERVICE_BLE_IS_READY',
        }),
    getCurrentPosition: () =>
        postMessageToNativeApp({
            type: 'LOCATION_SERVICE_GET_CURRENT_POSITION',
        }),
    getVisibleTags: (params = {}) =>
        postMessageToNativeApp({
            type: 'LOCATION_SERVICE_GET_VISIBLE_BLE_TAGS',
            payload: params,
        }),
};

const triggerHaptic = (
    payload: hapticPayload,
): Promise<defaultNativeAppResponse> =>
    postMessageToNativeApp({
        type: 'HAPTIC',
        payload,
    });

/**
 * Haptic feedback played by the native app.
 * Devices without a vibration motor resolve without any feedback.
 */
export const haptic: BridgeHaptic = {
    /**
     * A collision between UI elements, e.g. a button press or a drop.
     * @param style - intensity of the impact, `medium` by default
     */
    impact: (style = 'medium') => triggerHaptic({type: 'impact', style}),
    /**
     * The outcome of an action: `success`, `warning` or `error`.
     */
    notification: (notificationType) =>
        triggerHaptic({type: 'notification', notificationType}),
    /**
     * A change of selection, e.g. scrolling a picker or toggling an option.
     */
    selection: () => triggerHaptic({type: 'selection'}),
};
